import { matchFuzzy, matchStrictSubset } from '~/core/util/string';

import { Service } from './Service';
import { Reasons, ServiceName } from './Reasons';

/**
 * Flatten the servicesObj into a hash
 *
 * @param servicesObj the Services Object
 */
export const getServices = (servicesObj: Record<string, Service[]>) =>
  Object.values(servicesObj).reduce<Record<string, Service>>(
    (optionsValues, services) => ({
      ...optionsValues,
      ...services.reduce(
        (optionsValues, service) => ({
          ...optionsValues,
          [service.name]: service,
        }),
        optionsValues
      ),
    }),
    {}
  );

/**
 * Gets a Service by name from servicesObj, if it exists.
 *
 * @param name a service name (not required to be V1)
 * @param servicesObj the Services Object
 */
export const getServiceByName = (
  name: string,
  servicesObj: Record<string, Service[]>
): Service | undefined => {
  const services = getServices(servicesObj);
  return services[name];
};

/**
 * Returns true if the service name is V1
 *
 * @param serviceName a service name
 */
export const isV1Service = (name: string) => {
  const serviceName = ServiceName[name as ServiceName];
  return Boolean(serviceName);
};

/**
 * Flatten the Reasons hash into a hash from Reason to Service Name
 */
export function getAllReasons(reasons: Record<string, string[]> = {}) {
  return Object.entries(reasons).reduce<Record<string, string>>(
    (reasonsValues, [name, reasons]) => {
      return {
        ...reasonsValues,
        ...reasons.reduce(
          (reasonsValues, reason) => ({
            ...reasonsValues,
            [reason]: name,
          }),
          reasonsValues
        ),
      };
    },
    {}
  );
}

/**
 * Matches a string with a Services Object and Reasons Object
 */
export function matchReasons({
  matchValue,
  reasons = Reasons,
  count = 0,
  strictSubset = false,
  minimumRatio = 0,
}: {
  /** Value to match with Reasons */
  matchValue: string;
  /** Reasons for visit */
  reasons?: typeof Reasons;
  /** Maximum number of results */
  count?: number;
  /** Use strict subset matching */
  strictSubset?: boolean;
  /** Minimum Levenshtein Ratio for fuzzy match */
  minimumRatio?: number;
}) {
  const allReasons = getAllReasons(reasons);
  const matches = strictSubset
    ? matchStrictSubset(matchValue, Object.keys(allReasons))
    : matchFuzzy(matchValue, Object.keys(allReasons), minimumRatio);
  const matchServices = matches.reduce<Record<string, boolean>>(
    (matchKeys, match) => ({
      ...matchKeys,
      [allReasons[match]]: true,
    }),
    {}
  );
  if (count) {
    return Object.keys(matchServices).slice(0, count);
  }
  return Object.keys(matchServices);
}
